import { Slice } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';

import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import { purePmInEditorDirectStreamingPluginKey } from '../pluginKey';
import { updateStreamPositions, metaTransaction } from '../transforms';
import type { PurePmInEditorDirectStreamingPluginState } from '../types';
import {
	resolveContentFragment,
	fragmentIsHealthy,
	calculateTelepointerPos,
} from '../utils/adfConversion';
import { clampPosition } from '../utils/positionValidation';

import { calculateListAdjustment, calculateBlockMarkAdjustment } from './positionAdjustments';
import { mapStartPos, mapToPos } from './positionMapping';
import { calculateSliceOpenStartEndAdjustment } from './sliceAdjustments';

/**
 * Builds transaction for committing final AI content.
 * This is Phase 2 of the two-dispatch finalize pattern.
 * Should be called AFTER restore transaction has been dispatched.
 *
 * Note: This closely matches the original commitFinalResponse in useInEditorDirectStreaming.
 * The slice adjustment (openStart/openEnd) is applied to prevent unwanted node merging.
 */
export function buildCommitTransaction(state: EditorState): Transaction | null {
	const sessionState = aiSessionContextPluginKey.getState(state);
	if (!sessionState) {
		return null;
	}
	const { adfResponse, markdown, idMap, responseVersion } = sessionState;

	const streamingState = purePmInEditorDirectStreamingPluginKey.getState(state);
	const decorationPluginState = aiExperienceDecorationPluginKey.getState(state);
	const { positions } = decorationPluginState ?? {};

	if (!streamingState) {
		return null;
	}

	// When decoration positions are unavailable (e.g. cleared after the restore transaction
	// remapped them away for `replaceNode` commands on empty table cells), we still need to
	// mark the stream as finalised so `isStreaming` returns to `false` and
	// `endExperiencePromise` can resolve. Fall through to `buildCommitStateOnly` which
	// sets `isFinalStream: true` without needing document positions.
	if (!positions) {
		return buildCommitStateOnly(state, streamingState, sessionState.responseVersion);
	}

	// After restore, the document is back to original state
	// Use the positions from streaming state (which were mapped through restore)
	const { startPos, userContentEndPos, originalUserContentSlice, aiContentSlice } = streamingState;

	if (startPos === undefined || userContentEndPos === undefined) {
		return buildCommitStateOnly(state, streamingState, responseVersion);
	}

	// Resolve final content using the prioritised conversion chain:
	// ADF → experiment-gated markdown → free-gen markdown fallback
	const finalFragment = resolveContentFragment(state.schema, adfResponse, markdown, idMap);

	// IMPORTANT: Prefer the slice adjusted during the update phase when it still matches the
	// final adfResponse. This preserves the existing behavior for edge cases like inserting
	// a heading at the start of a paragraph. If the final response changed after the last
	// stream update, rebuild the slice from the final adfResponse so we don't commit stale
	// streamed content.
	const canReuseStreamedSlice =
		!!aiContentSlice &&
		!!finalFragment &&
		fragmentIsHealthy(finalFragment) &&
		aiContentSlice.content.eq(finalFragment);

	let slice = canReuseStreamedSlice ? aiContentSlice : undefined;

	if (!slice) {
		if (!finalFragment || !fragmentIsHealthy(finalFragment)) {
			return buildCommitStateOnly(state, streamingState, responseVersion);
		}

		slice = Slice.maxOpen(finalFragment);
		if (!slice.content.firstChild) {
			slice = Slice.empty;
		}
	}

	// Use positions from streaming state
	const docSize = state.doc.content.size;
	let workingStartPos = clampPosition(startPos, docSize);
	let workingUserContentEndPos = clampPosition(userContentEndPos, docSize);

	// List adjustment (matching original)
	const listAdjustment = calculateListAdjustment({
		state,
		fragment: slice.content,
		startPos: workingStartPos,
		userContentEndPos: workingUserContentEndPos,
	});
	if (listAdjustment.adjusted) {
		workingStartPos = listAdjustment.startPos;
		workingUserContentEndPos = listAdjustment.userContentEndPos;
	}

	// Block mark adjustment — when startPos is inside a paragraph/heading's text
	// and the replacement is the same block type with marks, move startPos back
	// to include the block node so block-level marks (e.g. alignment) are applied.
	const blockMarkAdjustment = calculateBlockMarkAdjustment({
		state,
		fragment: slice.content,
		startPos: workingStartPos,
		userContentEndPos: workingUserContentEndPos,
	});
	if (blockMarkAdjustment.adjusted) {
		workingStartPos = blockMarkAdjustment.startPos;
	}

	let tr = state.tr;
	const streamTimeStamp = streamingState.streamTimeStamp;
	const safeEndPos = Math.max(workingStartPos, Math.min(workingUserContentEndPos, docSize));

	// Adjust slice openStart/openEnd to prevent unwanted node merging
	// (e.g. losing a panel wrapper when inserting before an existing node)
	slice = calculateSliceOpenStartEndAdjustment({
		state,
		slice,
		startPos: workingStartPos,
		toPos: safeEndPos,
	});

	// Apply final content - matching original commitFinalResponse logic
	try {
		const { paragraph } = tr.doc.type.schema.nodes;
		// If we have a lone paragraph - replaceRange will not replace with a paragraph
		const isSingleParagraph =
			slice.content.firstChild === slice.content.lastChild &&
			slice.content.firstChild?.type === paragraph;

		if (isSingleParagraph && workingStartPos < docSize) {
			const $from = tr.doc.resolve(workingStartPos);
			const isEmptyParagraph = $from.parent.type === paragraph && $from.parent.content.size === 0;

			if (isEmptyParagraph && slice.content.firstChild) {
				tr = tr.replaceRangeWith(workingStartPos, safeEndPos, slice.content.firstChild);
			} else {
				tr = tr.replaceRange(workingStartPos, safeEndPos, slice);
			}
		} else {
			tr = tr.replaceRange(workingStartPos, safeEndPos, slice);
		}

		tr = metaTransaction({ tr, isFinalStream: true, stackTime: streamTimeStamp });
	} catch {
		return buildCommitStateOnly(state, streamingState, responseVersion);
	}

	// Set selection at end of inserted content
	try {
		tr = tr.setSelection(
			TextSelection.create(tr.doc, Math.min(tr.selection.to, tr.doc.content.size)),
		);
	} catch {
		// Invalid position - keep default selection
	}

	// Skip history if content unchanged
	if (originalUserContentSlice) {
		try {
			const newDocSize = tr.doc.content.size;
			const finalStartPos = clampPosition(mapStartPos({ pos: startPos, tr }), newDocSize);
			const finalEndPos = clampPosition(mapToPos({ pos: userContentEndPos, tr }), newDocSize);
			const newSlice = tr.doc.slice(finalStartPos, finalEndPos);
			if (newSlice.eq(originalUserContentSlice)) {
				tr = tr.setMeta('addToHistory', false);
			}
		} catch {
			// Position mapping failed - add to history as normal
		}
	}

	// Map final positions for telepointer
	const finalDocSize = tr.doc.content.size;
	const mappedStartPos = clampPosition(mapStartPos({ pos: startPos, tr }), finalDocSize);
	const mappedToPos = clampPosition(mapToPos({ pos: userContentEndPos, tr }), finalDocSize);

	const telepointerPos = calculateTelepointerPos(tr.doc, mappedStartPos, mappedToPos);

	tr = updateStreamPositions({
		aiContentEndPos: mappedToPos,
		telepointerPos,
		isFinalStream: true,
		lastProcessedVersion: responseVersion,
	})(tr);

	return tr;
}

function buildCommitStateOnly(
	state: EditorState,
	streamingState: PurePmInEditorDirectStreamingPluginState,
	responseVersion: number,
): Transaction {
	let tr = state.tr;
	tr = updateStreamPositions({
		aiContentEndPos: streamingState.aiContentEndPos ?? streamingState.startPos ?? 0,
		telepointerPos: streamingState.telepointerPos ?? streamingState.startPos ?? 0,
		isFinalStream: true,
		lastProcessedVersion: responseVersion,
	})(tr);
	return tr;
}
